import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
  IconButton,
} from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import InputText from "components/_refactor/text/input";
import UploadFile from "components/_refactor/dropzone";
import CloseIcon from "@mui/icons-material/Close";

const MAX_MEDIA = 4;
const MAX_MEDIA_SIZE = 1024 * 1024; // 1MB

export default function EditProductDialog({
  formType,
  onClose,
  selectedProduct,
  productsList,
  setProductsList,
  motivoRequerEvidencia,
}) {
  const [quantity, setQuantity] = useState(selectedProduct?.quantity);
  const [productImages, setProductImages] = useState(selectedProduct?.images || []);
  const [submitting, setSubmitting] = useState(false);

  const snackbar = useSnackbar();

  const validateFile = (file) => {
    if (file.size > MAX_MEDIA_SIZE) {
      return {
        code: "file-too-large",
        message: `Tamanho máximo permitido é 1MB. O arquivo ${file.name} tem ${(file.size / 1024 / 1024).toFixed(2)}MB`,
      };
    }
    return null;
  };

  const handleMediaUpload = (files) => {
    if (!files || files.length === 0) {
      setProductImages([]);
      return;
    }
    if (files.length > MAX_MEDIA) {
      snackbar.showMessage(`Máximo de ${MAX_MEDIA} arquivos permitido`, { severity: "error" });
      return;
    }
    const validFiles = files.filter((file) => file.size <= MAX_MEDIA_SIZE);
    const mediaPromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const isVideo = file.type.startsWith("video/");
          resolve({
            base64: reader.result,
            name: file.name,
            extension: file.name.split(".").pop().toLowerCase(),
            type: isVideo ? "video" : "image",
            file,
          });
        };
        reader.readAsDataURL(file);
      });
    });
    Promise.all(mediaPromises).then(setProductImages);
  };

  const submit = async () => {
    setSubmitting(true);

    if (formType === "delete") {
      const list = productsList.filter((product) => product.idProduto !== selectedProduct.idProduto);
      setProductsList(list);
      setSubmitting(false);
      onClose();
      return;
    }

    if (formType === "edit") {
      if (motivoRequerEvidencia && (!productImages || productImages.length === 0)) {
        snackbar.showMessage("Para o motivo da solicitação é obrigatório informar evidência (imagem ou vídeo).", {
          severity: "error",
        });
        setSubmitting(false);
        return;
      }
      const updated = {
        ...selectedProduct,
        idTableProduct: selectedProduct?.idTableProduct,
        product: selectedProduct?.product,
        name: selectedProduct?.name,
        idProduto: selectedProduct?.idProduto,
        quantity: quantity,
        images: productImages,
      };

      const list = productsList.map((product) =>
        product.idProduto === selectedProduct.idProduto ? updated : product
      );
      setProductsList(list);
      setSubmitting(false);
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth={"lg"}>
      <DialogTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" color="primary">
            {`${formType === "edit" ? "Editar" : "Excluir"} Produto`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ padding: "1rem 1.5rem 0" }}>
        {formType === "delete" ? (
          <>
            <Typography variant="caption">Confirmar exclusão do Produto {selectedProduct.idProduto} - {selectedProduct.name}?</Typography>
          </>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h4">Produto</Typography>
              <Typography variant="caption">
                {selectedProduct.idProduto} - {selectedProduct.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <InputText
                label="Quantidade"
                disabled={submitting}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ min: 1 }}
                type="number"
              />
            </Grid>
            {formType === "edit" && (
              <Grid item xs={12}>
                <UploadFile
                  multiple
                  accept="image/*,video/*"
                  message={
                    motivoRequerEvidencia
                      ? "Evidência (imagens ou vídeos) * Obrigatório para o motivo da solicitação (máximo 4 arquivos, 1MB cada)"
                      : "Selecione imagens ou vídeos (máximo 4 arquivos, 1MB cada)"
                  }
                  validator={validateFile}
                  onChangeFiles={handleMediaUpload}
                  disabled={submitting || productImages.length >= MAX_MEDIA}
                />
              </Grid>
            )}
          </Grid>
        )}
        {submitting && (
          <div style={{ marginTop: "1rem" }}>
            <span>{`${
              formType === "edit" ? "Atualizando" : "Deletando"
            } Produto...`}</span>
            <LinearProgress />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          color="default"
          variant="contained"
          type="submit"
          disabled={
            formType === "delete"
              ? submitting
              : submitting ||
                !quantity ||
                (motivoRequerEvidencia && (!productImages || productImages.length === 0))
          }
          onClick={submit}
        >
          {formType === "delete" ? "Confirmar" : "Atualizar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
